import { motion } from 'framer-motion';
import React, { type FC, type PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';

import { Backdrop } from '../../atoms/backdrop/Backdrop';

export type DrawerWrapperProps = PropsWithChildren<{
	drawerPosition?: 'left' | 'right';
	footerChildren?: React.ReactNode;
	headerChildren?: React.ReactNode;
	isRTL: boolean;
	onCloseDrawer: () => void;
	showDrawer: boolean;
	testID?: string;
}>;

const oppositePositions = {
	left: 'right',
	right: 'left'
};

export const DrawerWrapper: FC<DrawerWrapperProps> & { width: number } = ({
	children,
	drawerPosition,
	footerChildren,
	headerChildren,
	isRTL,
	onCloseDrawer,
	showDrawer,
	testID = 'drawer-wrapper'
}) => {
	const computedDrawerPosition = useMemo(() => {
		return drawerPosition ? (isRTL ? oppositePositions[drawerPosition] : drawerPosition) : isRTL ? 'right' : 'left';
	}, [drawerPosition, isRTL]);

	const openPosition = {
		[computedDrawerPosition]: 0
	};

	const closePosition = {
		[computedDrawerPosition]: '-110%'
	};

	return (
		<Backdrop clickHandler={() => onCloseDrawer?.()} visible={showDrawer}>
			{/* Side Drawer */}
			<S.DrawerContainer
				animate={showDrawer ? 'show' : 'hide'}
				as={motion.div}
				data-testid={testID}
				initial="hide"
				variants={{
					hide: closePosition,
					show: openPosition
				}}
			>
				{/* Drawer content */}
				{showDrawer && (
					<>
						<S.HeaderContainer>{headerChildren}</S.HeaderContainer>
						<S.ContentContainer>{children}</S.ContentContainer>
						{footerChildren && <S.FooterContainer>{footerChildren}</S.FooterContainer>}
					</>
				)}
			</S.DrawerContainer>
		</Backdrop>
	);
};

DrawerWrapper.width = 400;
namespace S {
	export const DrawerContainer = styled.div`
		display: flex;
		flex-direction: column;
		position: absolute;
		top: 0;
		width: ${DrawerWrapper.width}px;
		height: 100vh;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.large};
		box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);

		${({ theme }) => `${theme.breakpoints.queries.only.mobile} {
			width: 90%;
		}`};
	`;

	export const HeaderContainer = styled.div`
		flex-shrink: 0;
	`;

	export const ContentContainer = styled.div`
		flex-grow: 1;
		overflow-y: auto;
	`;
	export const FooterContainer = styled.div`
		flex-shrink: 0;
	`;
}
